import { MeasureCardRow } from 'components/MeasureCardRow';
import { TimeSeriesChartPageSection } from 'components/chart/TimeSeriesChartPageSection';
import { ToggleButtonGroup } from '@mui/material';
import { StyledToggleButton } from 'components/StyledToggleButton';
import { FormattedMessage, useIntl } from 'react-intl';
import { attributeToToggleTranslationKey, getAttributeToggleTrackingName } from 'pages/entities/entities';
import { BreakdownTable } from 'pages/entities/components/breakdown-table/BreakdownTable';
import { useDatePickerLabels } from 'hooks/useDatePickerLabels';
import { Attribute, useModelService, Measure } from 'services/model/model.service';
import { useDataRequest } from 'hooks/useDataRequest';
import { Filters } from 'services/data.service';
import { useEffect, useState } from 'react';
import { createTimeSeriesChartConfig } from 'utils/createTimeSeriesChartConfig';
import { LineChartRenderer } from 'components/chart/renderer/LineChartRenderer';
import { useKpiData, useTimeSeries } from 'pages/entities/queryHooks';
import { useLocation } from 'react-router-dom';
import { trackMatomoEvent } from 'services/matomo.service';
import { getTrackingPage, MATOMO_ACTIONS } from 'utils/matomoTracking';
import { useInfoDrawer } from 'context/InfoDrawerContext';
import { PerformanceEntityInfoContent } from 'pages/entities/components/PerformanceEntityInfoContent';

export interface PerformanceTabProps {
  filterDecorator: (filter: Filters) => Filters;
  availableTables: Attribute[];
}

export function PerformanceTab({ filterDecorator, availableTables }: PerformanceTabProps) {
  const intl = useIntl();
  const labelContext = useDatePickerLabels();
  const location = useLocation();
  const { setContent } = useInfoDrawer();

  useEffect(() => {
    setContent({
      titleKey: 'entities.lens.performance',
      content: <PerformanceEntityInfoContent />,
    });
    return () => setContent(null);
  }, [setContent]);

  const { getMeasuresByMetrics, getAttribute } = useModelService();
  const requestData = useDataRequest();
  const adjustedRequestData = { ...requestData, filters: filterDecorator(requestData.filters) };

  const orderMeasures = getMeasuresByMetrics(['ordQty', 'retQtyRate', 'netSlsQty', 'netSlsGmvVal'], 'OD');

  // add parent specific filters to request data
  const [timeSeriesMeasure, setTimeSeriesMeasure] = useState(orderMeasures[0]);

  const dashboardState = useKpiData(adjustedRequestData);

  const [availableAttributeTables, setAvailableAttributeTables] = useState<Attribute[]>();
  const [selectedAttributeTable, setSelectedAttributeTable] = useState<Attribute>();

  useEffect(() => {
    setSelectedAttributeTable(availableTables[0]);
    setAvailableAttributeTables(availableTables);
  }, [availableTables]);

  const { data: timeSeriesData } = useTimeSeries(adjustedRequestData, timeSeriesMeasure.code);

  const timeSeriesLineRenderer = timeSeriesData
    ? new LineChartRenderer(
        createTimeSeriesChartConfig({
          getAttribute,
          measures: [timeSeriesMeasure],
          request: adjustedRequestData,
          intl,
          response: timeSeriesData,
          labelContext,
        })
      )
    : null;

  const timeSeriesMeasuresChangeHandler = (measure: Measure) => () => {
    if (measure.code !== timeSeriesMeasure.code) {
      setTimeSeriesMeasure(measure);
    }
  };

  const onTableToggle = (_event: React.MouseEvent<HTMLElement>, value: Attribute | null) => {
    if (!value || value.code === selectedAttributeTable?.code) {
      return;
    }

    trackMatomoEvent({
      category: getTrackingPage(location.pathname),
      action: MATOMO_ACTIONS.TABLE_TOGGLE,
      name: getAttributeToggleTrackingName(value.code),
    });
    setSelectedAttributeTable(value);
  };

  return (
    <>
      {dashboardState.data && (
        <MeasureCardRow
          measures={orderMeasures}
          testid="pro-performance-measure-card-set"
          totalsData={dashboardState.data}
          selectedMeasureCode={timeSeriesMeasure.code}
          onMeasureSelect={timeSeriesMeasuresChangeHandler}
        />
      )}
      <TimeSeriesChartPageSection
        timeSeriesMeasure={timeSeriesMeasure}
        timeSeriesRenderer={timeSeriesLineRenderer}
        showGroupByToggle={false}
      />
      {availableAttributeTables && (
        <ToggleButtonGroup
          className="ml-auto mb-auto mt-auto"
          exclusive
          value={selectedAttributeTable ?? ''}
          onChange={onTableToggle}
        >
          {availableAttributeTables.map(option => (
            <StyledToggleButton
              key={option.name}
              size="small"
              className="whitespace-nowrap text-xs"
              value={option}
              sx={{ color: '#1A1A1A' }}
            >
              <FormattedMessage
                id={`entities.name.singular.${attributeToToggleTranslationKey[option.code] ?? option.code}`}
              />
            </StyledToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
      {selectedAttributeTable && (
        <BreakdownTable keyAttribute={selectedAttributeTable} filterDecorator={filterDecorator} />
      )}
    </>
  );
}
